import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { ChevronRight, FileText, Users } from 'lucide-react';

export const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1700819000398-d1b72be76844?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMDNEJTIwZ2VvbWV0cmljJTIwc2hhcGVzJTIwc2lsdmVyJTIwYmx1ZSUyMGRhcmslMjBiYWNrZ3JvdW5kJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzExMzAxNjB8MA&ixlib=rb-4.1.0&q=85')` 
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 hero-gradient" />

      {/* Radial glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Content */}
          <div className="space-y-8 animate-fade-in">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 text-primary text-sm font-medium">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              Builders Program Open
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight" data-testid="hero-headline">
              {t('hero.headline')}
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-xl" data-testid="hero-subheadline">
              {t('hero.subheadline')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#top10" data-testid="cta-top10">
                <Button size="lg" className="w-full sm:w-auto glow-primary group">
                  {t('hero.cta_top10')}
                  <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              
              <Link to="/apply" data-testid="cta-proposal">
                <Button size="lg" variant="outline" className="w-full sm:w-auto group">
                  <FileText className="w-5 h-5 mr-2" />
                  {t('hero.cta_proposal')}
                </Button>
              </Link>
            </div>

            {/* Secondary CTA */}
            <Link 
              to="/apply?type=partner" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
              data-testid="cta-partner"
            >
              <Users className="w-5 h-5" />
              <span>{t('hero.cta_partner')}</span>
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Right - Visual */}
          <div className="hidden lg:block relative animate-slide-right stagger-2">
            <div className="relative w-full aspect-square">
              {/* Decorative elements */}
              <div className="absolute top-10 right-10 w-64 h-64 bg-primary/20 rounded-full blur-[80px]" />
              <div className="absolute bottom-10 left-10 w-48 h-48 bg-purple-500/20 rounded-full blur-[60px]" />
              
              {/* Stats cards floating */}
              <div className="absolute top-20 right-0 glass-card rounded-xl p-4 animate-fade-in stagger-3">
                <div className="text-3xl font-bold text-white">10+</div>
                <div className="text-sm text-slate-400">Strategic dApps</div>
              </div>
              
              <div className="absolute bottom-20 left-0 glass-card rounded-xl p-4 animate-fade-in stagger-4">
                <div className="text-3xl font-bold text-primary">$1M+</div>
                <div className="text-sm text-slate-400">Grants Available</div>
              </div>

              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 glass-card rounded-xl p-6 animate-fade-in stagger-5">
                <div className="text-2xl font-bold text-white mb-2">UNI2Chain</div>
                <div className="text-sm text-slate-400">Next-Gen Infrastructure</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-xs text-slate-500">Scroll</span>
        <div className="w-6 h-10 rounded-full border-2 border-slate-600 flex items-start justify-center p-2">
          <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
        </div>
      </div>
    </section>
  );
};
